import React, { useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2 } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { timeAgo } from "@/lib/communities";

const PLATFORMS = ["PS5", "Xbox", "PC"];

export default function LFG() {
    const { user } = useAuth();
    const { data: communities } = useFetch("/communities");
    const { data: posts, reload } = useFetch("/lfg");
    const [form, setForm] = useState({ community_key: "CODERS_PLAYGROUND", platform: "PS5", role: "", experience: "Any", team_size: 3, playstyle: "Any", message: "", post_to_discord: true });
    if (!communities || !posts) return <Loader />;
    const upd = (k, v) => setForm({ ...form, [k]: v });
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k;

    const post = async () => {
        if (!form.role) { toast.error("Role required"); return; }
        try { await api.post("/lfg", { ...form, team_size: +form.team_size }); toast.success("LFG posted"); setForm({ ...form, role: "", message: "" }); reload(); }
        catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    };

    return (
        <div data-testid="player-lfg">
            <PageHeader title="Looking For Group" subtitle="Find squadmates across PS5, Xbox and PC." />
            <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
                <SectionCard title="Post LFG" testId="lfg-composer">
                    <div className="space-y-3">
                        <Select value={form.community_key} onValueChange={(v) => upd("community_key", v)}>
                            <SelectTrigger data-testid="lfg-community" className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent>{communities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="grid grid-cols-2 gap-2">
                            <Select value={form.platform} onValueChange={(v) => upd("platform", v)}>
                                <SelectTrigger data-testid="lfg-platform" className="bg-background border-border"><SelectValue /></SelectTrigger>
                                <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input type="number" value={form.team_size} onChange={(e) => upd("team_size", e.target.value)} className="bg-background border-border" placeholder="Team size" />
                        </div>
                        <Input data-testid="lfg-role" placeholder="Role (Builder, PVP...)" value={form.role} onChange={(e) => upd("role", e.target.value)} className="bg-background border-border" />
                        <Input placeholder="Playstyle" value={form.playstyle} onChange={(e) => upd("playstyle", e.target.value)} className="bg-background border-border" />
                        <Textarea placeholder="Message" value={form.message} onChange={(e) => upd("message", e.target.value)} className="bg-background border-border" rows={2} />
                        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                            <Checkbox data-testid="lfg-discord" checked={form.post_to_discord} onCheckedChange={(v) => upd("post_to_discord", !!v)} /> Post to Discord LFG
                        </label>
                        <Button data-testid="lfg-post" onClick={post} className="w-full font-heading uppercase bg-hazard text-black hover:bg-hazard/90"><Plus size={15} className="mr-1" /> Post</Button>
                    </div>
                </SectionCard>

                <div className="space-y-3">
                    {posts.map((p) => (
                        <div key={p.id} data-testid={`lfg-${p.id}`} className="tactical-card rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Users size={16} className="text-hazard" />
                                    <span className="font-heading font-bold uppercase text-foreground">{p.username}</span>
                                    <span className="label-mono text-muted-foreground">{p.platform} · {nameFor(p.community_key)}</span>
                                </div>
                                {p.user_email === user?.email && (
                                    <button onClick={async () => { await api.delete(`/lfg/${p.id}`); reload(); }} className="text-danger/70 hover:text-danger"><Trash2 size={14} /></button>
                                )}
                            </div>
                            <div className="text-sm text-foreground mt-2">Role: <span className="text-hazard">{p.role}</span> · Looking for {p.team_size} · {p.playstyle}</div>
                            <p className="text-sm text-muted-foreground mt-1">{p.message}</p>
                            <span className="label-mono text-muted-foreground">{timeAgo(p.created_at)}</span>
                        </div>
                    ))}
                    {posts.length === 0 && <div className="text-muted-foreground">No LFG posts yet.</div>}
                </div>
            </div>
            <Footer />
        </div>
    );
}
